import { QuotePdfMenu } from "./QuotePdfMenu";
import { needsCheck } from "./QuoteCheckModal";
import { QuoteRequestDetail } from "./QuoteRequestDetail";
import { QuoteCompareBar } from "./QuoteCompareBar";
import { isPendingRequest, WaitBadge } from "./NewRequestsCounter";
import { useMemo, useState } from "react";
import {
  CheckCircle2, Crown, ExternalLink, FileText, Mail, MessageCircle, Pencil, Scale, Search, Trash2,
} from "lucide-react";
import { money, QUOTE_STATUSES, QUOTE_STATUS_COLORS } from "../lib/finance";
import { waTarget } from "../lib/wa";
import { PageTitle, Empty, Modal } from "./common";

export function QuotesPage({ quotes, customers, branches, userName = "", setEditingQuote, onDelete, onConvert, onOpenOnlineLink, onOpenPdf, onEmail, onPdfSent, onCheck, initialFilter = "all" }) {
  const [filter, setFilter] = useState(initialFilter);
  const [sel, setSel] = useState([]);
  const toggleSel = (id) => setSel((x) => (x.includes(id) ? x.filter((i) => i !== id) : [...x, id]));
  const [q, setQ] = useState("");
  const [compareOpen, setCompareOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const buildQuoteMsg = (qt) => {
    const greet = qt.customerName ? `Merhaba ${qt.customerName},` : "Merhaba,";
    const branchText = qt.branch ? `${qt.branch} ` : "";
    const plateText = qt.plate ? `${qt.plate} plakalı aracınız için ` : "";
    const priceText = qt.premium ? `Teklif tutarınız: ${new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(qt.premium)}.` : "Teklifiniz hazır.";
    const signoff = userName ? `\n\nİyi günler,\n${userName}` : "";
    return `${greet}\n\n${plateText}${branchText}sigortanız için hazırladığım teklifi iletiyorum. ${priceText} Detaylar için uygun olduğunuzda görüşelim.${signoff}`;
  };
  const filtered = useMemo(() => {
    return quotes.filter((qt) => {
      if (filter === "requests" ? qt.source !== "online_link" : filter !== "all" && qt.status !== filter) return false;
      if (!q) return true;
      const needle = q.toLocaleLowerCase("tr");
      return (
        (qt.customerName || "").toLocaleLowerCase("tr").includes(needle) ||
        (qt.branch || "").toLocaleLowerCase("tr").includes(needle) ||
        (qt.company || "").toLocaleLowerCase("tr").includes(needle) ||
        (qt.plate || "").toLocaleLowerCase("tr").includes(needle)
      );
    }).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [quotes, filter, q]);

  const counts = useMemo(() => {
    const c = { all: quotes.length, requests: quotes.filter((x) => x.source === "online_link").length, newRequests: quotes.filter(isPendingRequest).length };
    for (const s of QUOTE_STATUSES) c[s] = quotes.filter((qt) => qt.status === s).length;
    return c;
  }, [quotes]);

  return (
    <div className="space-y-8" data-testid="quotes-page">
      <PageTitle
        eyebrow="Satış"
        title="Teklifler"
        description="Potansiyel müşteriler için teklif aşamalarını yönetin ve kabul olanları poliçeye dönüştürün."
        action="Yeni teklif"
        actionTest="new-quote-button"
        onAction={() => setEditingQuote({})}
      />

      <div className="panel" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className={`chip ${filter === "all" ? "chip-active" : ""}`} data-testid="quote-filter-all" onClick={() => setFilter("all")}>Tümü · {counts.all}</button>
          <button className={`chip ${filter === "requests" ? "chip-active" : ""}`} data-testid="quote-filter-requests" onClick={() => setFilter("requests")}>Talepler · {counts.requests}{counts.newRequests > 0 && <b className="chip-new" data-testid="quote-requests-new">{counts.newRequests} bekleyen</b>}</button>
          {QUOTE_STATUSES.map((s) => (
            <button key={s} className={`chip ${filter === s ? "chip-active" : ""}`} data-testid={`quote-filter-${s}`} onClick={() => setFilter(s)}>{s} · {counts[s] || 0}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div className="search-field compact">
            <Search size={14} />
            <input data-testid="quote-search-input" placeholder="Müşteri, branş, plaka..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <button className="outline-button" data-testid="open-online-link-button" onClick={onOpenOnlineLink}><ExternalLink size={15} /> Online teklif linkim</button>
          <button className="outline-button" data-testid="open-compare-button" onClick={() => setCompareOpen(true)}><Scale size={15} /> Rakip karşılaştır</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <Empty text="Henüz teklif yok. 'Yeni teklif' ile başlayabilir veya online teklif linkinizi paylaşabilirsiniz." />
      ) : (
        <div className="panel">
          <QuoteCompareBar selected={sel.map((id) => quotes.find((x) => x.id === id)).filter(Boolean)} onClear={() => setSel([])}
            onSent={(ids) => { onPdfSent?.(ids); setSel([]); }} />
          <table className="data-table" data-testid="quotes-table">
            <thead>
              <tr>
                <th style={{ width: 28 }} title="Karşılaştırmak için seçin" />
                <th>Müşteri</th>
                <th>İletişim</th>
                <th>Branş / Plaka</th>
                <th>Tahmini Prim</th>
                <th>Durum</th>
                <th>Kaynak</th>
                <th>Tarih</th>
                <th style={{ textAlign: "right" }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((qt) => (
                <tr key={qt.id} data-testid={`quote-row-${qt.id}`} className={sel.includes(qt.id) ? "row-selected" : ""}>
                  <td><input type="checkbox" checked={sel.includes(qt.id)} onChange={() => toggleSel(qt.id)} data-testid={`quote-select-${qt.id}`} aria-label="Karşılaştırmaya ekle" /></td>
                  <td>
                    <button type="button" className="quote-name-btn" onClick={() => setDetailId(qt.id)} data-testid={`quote-open-detail-${qt.id}`}><strong>{qt.customerName || "—"}</strong></button>
                    {isPendingRequest(qt) && <WaitBadge q={qt} />}
                    {qt.note && <div className="muted small quote-note-preview" style={{ marginTop: 4 }}>{qt.note}</div>}
                    {qt.note && <button type="button" className="text-button small" onClick={() => setDetailId(qt.id)} data-testid={`quote-read-note-${qt.id}`}>Detayı / notları gör</button>}
                  </td>
                  <td>
                    <div className="small">{qt.customerPhone || "—"}</div>
                    <div className="small muted">{qt.customerEmail || ""}</div>
                  </td>
                  <td>
                    <div>{qt.branch || "—"}</div>
                    {qt.plate && <div className="small muted">{qt.plate}</div>}
                    {qt.ruhsatSeriNo && <div className="small muted">Ruhsat: {qt.ruhsatSeriNo}</div>}
                    {qt.tcKimlik && <div className="small muted">TC: {qt.tcKimlik}</div>}
                  </td>
                  <td>{qt.premium ? money(qt.premium) : "—"}{qt.offers?.length > 1 && <div className="small muted" data-testid={`quote-offers-count-${qt.id}`}>{qt.offers.length} şirket · {qt.chosenOfferId ? "seçilen" : "en düşük"}</div>}</td>
                  <td>
                    <span className={`badge badge-${QUOTE_STATUS_COLORS[qt.status] || "slate"}`} data-testid={`quote-status-${qt.id}`}>{qt.status}</span>
                    {qt.customerChoiceAt && qt.status === "Kabul" && <span className="choice-badge" title={qt.customerChoiceNote ? `Müşteri notu: ${qt.customerChoiceNote}` : "Müşteri karşılaştırma linkinden seçti"} data-testid={`quote-customer-choice-${qt.id}`}><CheckCircle2 size={11} /> Müşteri seçti</span>}
                    {qt.customerChoiceNote && <div className="muted small" data-testid={`quote-choice-note-${qt.id}`}>“{qt.customerChoiceNote}”</div>}
                    {needsCheck(qt) && <button type="button" className="check-chip" onClick={() => onCheck?.(qt.id)} data-testid={`quote-check-open-${qt.id}`}>Son kontrol</button>}
                    {qt.agentConfirmedAt && qt.status === "Kabul" && <span className="muted small" data-testid={`quote-checked-${qt.id}`}> · Kontrol edildi</span>}
                  </td>
                  <td>
                    {qt.source === "online_link" ? (
                      <span className="badge badge-blue" title="Online teklif linkinden">Online</span>
                    ) : (
                      <span className="muted small">Manuel</span>
                    )}
                    {!!(qt.pdfs && qt.pdfs.length) && (
                      <button type="button" className="ghost-button" style={{ marginLeft: 6 }} data-testid={`quote-pdf-view-${qt.id}`} onClick={() => onOpenPdf && onOpenPdf(qt.id, qt.pdfs[0])} title={qt.pdfs[0].filename}>
                        <FileText size={13} /> PDF{qt.pdfs.length > 1 ? ` ×${qt.pdfs.length}` : ""}
                      </button>
                    )}
                  </td>
                  <td className="small muted">{qt.createdAt ? new Date(qt.createdAt).toLocaleDateString("tr-TR") : ""}</td>
                  <td style={{ textAlign: "right" }}>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "nowrap", alignItems: "center" }}>
                      <QuotePdfMenu qt={qt} onSent={(x) => onPdfSent?.([x.id])} />
                      {waTarget(qt.customerPhone) && (
                        <a className="whatsapp-button small" data-testid={`quote-wa-${qt.id}`} href={`https://wa.me/${waTarget(qt.customerPhone)}?text=${encodeURIComponent(buildQuoteMsg(qt))}`} target="_blank" rel="noreferrer" title="WhatsApp'tan Gönder">
                          <MessageCircle size={13} /> WhatsApp
                        </a>
                      )}
                      {qt.customerEmail && (
                        <button type="button" className="outline-button small" data-testid={`quote-email-${qt.id}`} onClick={() => onEmail && onEmail(qt)} title="E-posta ile Gönder">
                          <Mail size={13} /> E-posta
                        </button>
                      )}
                      {qt.status !== "Dönüştürüldü" && qt.status !== "Reddedildi" && (
                        <button className="primary-button small" data-testid={`quote-convert-${qt.id}`} onClick={() => onConvert(qt)} title="Poliçeye dönüştür">
                          Poliçeye dönüştür
                        </button>
                      )}
                      <button className="ghost-button" data-testid={`quote-edit-${qt.id}`} onClick={() => setEditingQuote(qt)}><Pencil size={14} /></button>
                      <button className="ghost-button danger" data-testid={`quote-delete-${qt.id}`} onClick={() => onDelete(qt.id)}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {compareOpen && <QuoteCompareModal quotes={quotes} onClose={() => setCompareOpen(false)} />}
      {detailId && quotes.find((x) => x.id === detailId) && <QuoteRequestDetail quote={quotes.find((x) => x.id === detailId)} onEdit={setEditingQuote} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function QuoteCompareModal({ quotes, onClose }) {
  const groups = useMemo(() => {
    const byKey = {};
    for (const q of quotes) {
      const key = (q.plate || "").toUpperCase().replace(/\s+/g, "") || (q.customerName ? `KUPON:${q.customerName}` : "");
      if (!key) continue;
      if (q.status === "Dönüştürüldü" || q.status === "Reddedildi") continue;
      if (!byKey[key]) byKey[key] = [];
      byKey[key].push(q);
    }
    return Object.entries(byKey)
      .filter(([, arr]) => arr.length >= 2)
      .map(([key, arr]) => {
        const sorted = arr.slice().sort((a, b) => (Number(a.premium) || Infinity) - (Number(b.premium) || Infinity));
        return { key, label: arr[0].plate || arr[0].customerName, items: sorted };
      })
      .sort((a, b) => b.items.length - a.items.length);
  }, [quotes]);
  return (
    <Modal title="Rakip Teklif Karşılaştırma" onClose={onClose}>
      <div className="modal-body" data-testid="quote-compare-modal">
        {groups.length === 0 ? (
          <div className="muted small" style={{ padding: 24, textAlign: "center" }} data-testid="compare-empty">
            Aynı plakaya ait birden fazla aktif teklif bulamadım. Rakip karşılaştırma için aynı araç/kişi için farklı şirketlere en az 2 teklif girin.
          </div>
        ) : (
          <>
            <p className="muted small">Aynı araç/kişi için farklı şirketlerden alınan tekliflerin karşılaştırması. En ucuzu <strong style={{ color: "#eab308" }}>⭐</strong> ile işaretli.</p>
            {groups.map((g) => (
              <section key={g.key} className="panel" data-testid={`compare-group-${g.key}`} style={{ marginBottom: 14, padding: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <strong style={{ fontSize: 15 }}>{g.label}</strong>
                  <span className="badge badge-slate">{g.items.length} teklif</span>
                </div>
                <table className="data-table">
                  <thead><tr><th></th><th>Şirket</th><th>Branş</th><th>Brüt Prim</th><th>Net Prim</th><th>Durum</th></tr></thead>
                  <tbody>
                    {g.items.map((qt, idx) => {
                      const isWinner = idx === 0 && Number(qt.premium) > 0;
                      return (
                        <tr key={qt.id} data-testid={`compare-row-${qt.id}`} style={isWinner ? { background: "#fffbeb" } : {}}>
                          <td style={{ width: 24 }}>{isWinner ? <Crown size={14} color="#eab308" /> : ""}</td>
                          <td><strong>{qt.company || "—"}</strong></td>
                          <td>{qt.branch || "—"}</td>
                          <td>{qt.premium ? money(qt.premium) : "—"}</td>
                          <td>{qt.netPremium ? money(qt.netPremium) : "—"}</td>
                          <td><span className={`badge badge-${QUOTE_STATUS_COLORS[qt.status] || "slate"}`}>{qt.status}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>
            ))}
          </>
        )}
        <div className="modal-actions">
          <button type="button" className="primary-button" data-testid="compare-close" onClick={onClose}>Tamam</button>
        </div>
      </div>
    </Modal>
  );
}
